import type { TalkPerson } from './chat.ts'
import type { Id } from './view.ts'

/**
 * Names in table talk, as the room, local tables, Luna and screens all read them: whom a line speaks
 * to ("@Cyd", "Cyd, …", "what do you think, Cyd?"), read loosely ("Luna 2" is Luna II), and where a
 * line names the viewer (drawn in gold).
 */

const ROMAN: Readonly<Record<string, number>> = { i: 1, ii: 2, iii: 3, iv: 4, v: 5, vi: 6, vii: 7, viii: 8, ix: 9, x: 10 }
const ROMAN_OF: Readonly<Record<number, string>> = Object.fromEntries(Object.entries(ROMAN).map(([r, n]) => [n, r]))
const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/**
 * A name as a pattern matching it loosely: any case, a numeral after the first word as roman or
 * digits ("Luna II", "luna 2", "Luna2", "lunaii"), and the spaces between its words optional.
 */
function namePattern(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean)
  return words.map((w, i) => {
    const alts = [escape(w)]
    const lower = w.toLowerCase()
    if (i > 0 && ROMAN[lower]) alts.push(String(ROMAN[lower]))
    if (i > 0 && /^\d+$/.test(w) && ROMAN_OF[Number(w)]) alts.push(ROMAN_OF[Number(w)]!)
    return alts.length > 1 ? `(?:${alts.join('|')})` : alts[0]
  }).join('[\\s._-]*')
}

/** A name as a key for matching what someone is typing ("Luna II" is "luna2" and "lunaii"). */
export function nameKeys(name: string): string[] {
  const words = name.trim().toLowerCase().split(/\s+/).filter(Boolean)
  const plain = words.join('')
  const digits = words.map((w, i) => (i > 0 && ROMAN[w] ? String(ROMAN[w]) : w)).join('')
  const roman = words.map((w, i) => (i > 0 && /^\d+$/.test(w) && ROMAN_OF[Number(w)] ? ROMAN_OF[Number(w)] : w)).join('')
  return [...new Set([plain, digits, roman])]
}

/** One place a line names someone. */
export interface NameMention {
  readonly id: Id
  /** Where, and how long (the `@` included). */
  readonly at: number
  readonly length: number
  /** Written with `@`. */
  readonly tagged: boolean
  /** "Cyd's…": a name as a possessive speaks about them, not to them. */
  readonly possessive: boolean
}

/**
 * Where a line names people, in order: each name loosely (`namePattern`), longest names first and
 * never inside another's ("Luna 2" is Luna II, not also Luna), with or without `@`. Names shorter
 * than two letters never match (too many false alarms).
 */
export function namesIn(text: string, people: readonly TalkPerson[]): NameMention[] {
  const found: NameMention[] = []
  const taken: [number, number][] = []
  const byLength = [...people].filter((p) => Array.from(p.name.trim()).length >= 2).sort((a, b) => b.name.trim().length - a.name.trim().length)
  for (const p of byLength) {
    const re = new RegExp(`(^|[^\\p{L}\\p{N}_])(@?)(${namePattern(p.name)})(?![\\p{L}\\p{N}_])`, 'giu')
    for (const m of text.matchAll(re)) {
      const at = m.index! + m[1]!.length
      const end = at + m[2]!.length + m[3]!.length
      if (taken.some(([a, b]) => at < b && end > a)) continue
      taken.push([at, end])
      found.push({ id: p.id, at, length: end - at, tagged: m[2] === '@', possessive: /^['’]s?\b/iu.test(text.slice(end)) })
    }
  }
  return found.sort((a, b) => a.at - b.at)
}

/** Words a line may start with before the name it speaks to ("hey Cyd", "ok Cyd"). */
const LEAD = /^[\s"'“(]*(?:(?:hey|hi|hello|ok|okay|so|and|well|oh|yo)[\s,!.]*)?$/iu

/**
 * Whom a line speaks to, in the order named: anyone `@`-mentioned; whoever it starts with ("Cyd,
 * what are you saving for?", "hey Cyd…"); and, when it asks something (a question mark), anyone it
 * names ("what do you think, Cyd?"). A name inside an ordinary sentence ("Cyd's running away with
 * it", "trade with Cyd later") speaks to nobody.
 */
export function addressees(text: string, people: readonly TalkPerson[]): Id[] {
  const out: Id[] = []
  const asking = text.includes('?')
  for (const m of namesIn(text, people)) {
    const speaks = m.tagged || (!m.possessive && (asking || LEAD.test(text.slice(0, m.at))))
    if (speaks && !out.includes(m.id)) out.push(m.id)
  }
  return out
}

/** Whether a line speaks to `name` (one person; `addressees` for a table of them). */
export function addressed(text: string, name: string): boolean {
  return addressees(text, [{ id: '_', name }]).length > 0
}

/** The spans of a line that name `me` where it speaks to them, for drawing ("Cyd", in gold). */
export function nameParts(text: string, people: readonly TalkPerson[], me: Id): { text: string; name?: true }[] {
  if (!addressees(text, people).includes(me)) return [{ text }]
  const out: { text: string; name?: true }[] = []
  let from = 0
  for (const m of namesIn(text, people)) {
    if (m.id !== me) continue
    if (m.at > from) out.push({ text: text.slice(from, m.at) })
    out.push({ text: text.slice(m.at, m.at + m.length), name: true })
    from = m.at + m.length
  }
  if (from < text.length) out.push({ text: text.slice(from) })
  return out
}
